import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { filter, take } from 'rxjs';
import { AuthFacade, AuthUserData } from '../../../store/state/auth';

@Component({
  selector: 'app-brain-webchat',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrainWebchatComponent implements OnInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly authFacade = inject(AuthFacade);

  private script: HTMLScriptElement | null = null;
  private widget: HTMLElement | null = null;
  private shadowRootInterval: number | null = null;
  private destroyed = false;

  ngOnInit(): void {
    this.authFacade.userData$
      .pipe(
        filter((userData: AuthUserData): userData is AuthUserData => !!userData),
        take(1),
      )
      .subscribe((userData: AuthUserData) => {
        if (userData.isInternalUser) {
          this.loadBrainWidget();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearShadowRootInterval();
    this.widget?.remove();
    this.script?.remove();
  }

  private loadBrainWidget(): void {
    this.script = this.document.createElement('script');

    this.script.type = 'module';
    this.script.src = 'https://brain.prd.dia-apps.bosch.tech/assets/chatbot_component/brain-webchat-widget.js';

    this.script.addEventListener('load', () => {
      if (!this.destroyed) {
        this.createBrainWidget();
      }
    });

    this.script.addEventListener('error', () => {
      console.error('Brain webchat widget could not be loaded.');
    });

    this.document.body.appendChild(this.script);
  }

  private createBrainWidget(): void {
    this.widget = this.document.createElement('brain-webchat-widget');

    this.widget.setAttribute('brain-endpoint', 'https://ews-emea.api.bosch.com/it/application/dia-brain/v1/api');
    this.widget.setAttribute('brain-id', 'w3UcLVEFTI02');
    this.widget.setAttribute(
      'auth-redirect-uri',
      'https://brain.prd.dia-apps.bosch.tech/assets/chatbot_component/auth-callback.html',
    );
    this.widget.setAttribute('dark-mode', 'false');

    this.document.body.appendChild(this.widget);

    this.adjustFloatingButton(this.widget);
  }

  private adjustFloatingButton(widget: HTMLElement): void {
    let attempts = 0;
    const maxAttempts = 50;

    this.shadowRootInterval = window.setInterval(() => {
      attempts++;

      if (widget.shadowRoot) {
        this.clearShadowRootInterval();

        const style = this.document.createElement('style');

        style.textContent = `
          .floating-button {
            bottom: 50px !important;
          }
        `;

        widget.shadowRoot.appendChild(style);
        return;
      }

      if (attempts >= maxAttempts) {
        this.clearShadowRootInterval();
      }
    }, 100);
  }

  private clearShadowRootInterval(): void {
    if (this.shadowRootInterval !== null) {
      window.clearInterval(this.shadowRootInterval);
      this.shadowRootInterval = null;
    }
  }
}
